/**
 * Clientes e os tempos deles.
 *
 * A ficha existe para uma coisa acima de todas: guardar quanto aquela pessoa
 * leva em cada serviço. É o que o barbeiro carregava na cabeça e é o que faz a
 * agenda fechar certo.
 */

import type {
  BirthdayClientView,
  ClientPhotoView,
  ClientView,
  DurationSuggestionView,
  ServiceView,
} from '@barbearia/shared';
import { PHOTO_MOMENT_LABELS } from '@barbearia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ScreenHeader } from '../../App.tsx';
import { Avatar, PhotoPicker, ZoomableAvatar } from '../../components/PhotoPicker.tsx';
import {
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  Field,
  Sheet,
  Spinner,
  TextArea,
} from '../../components/ui.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { useTenant } from '../../lib/contexts.tsx';
import { dayAndMonth, duration, phone as formatPhone } from '../../lib/format.ts';
import { PHOTO_RESIZE } from '../../lib/image.ts';
import { ClientSeriesSection } from './Series.tsx';
import { NewWaitlistSheet } from './Waitlist.tsx';

interface ClientSummary {
  id: string;
  name: string;
  phone: string;
  phoneFormatted: string;
  totalNoShows: number;
  lastVisitAt: string | null;
  hasAccount: boolean;
  /** Quantos serviços já têm tempo próprio para este cliente. */
  customDurations: number;
  photoUrl: string | null;
  archivedAt: string | null;
  approved: boolean;
}

export function Clients() {
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['clients', search, includeArchived],
    queryFn: () =>
      api.get<{ clients: ClientSummary[] }>('/clients', { search, includeArchived, limit: 100 }),
  });

  return (
    <div className="flex min-h-full flex-col pb-28">
      <ScreenHeader title="Clientes" subtitle={`${data?.clients.length ?? 0} cadastrados`} />

      <div className="px-4 py-3">
        <Field
          label="Buscar"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Nome ou telefone"
          autoCapitalize="words"
        />

        {/* Sem isto, arquivar é caminho sem volta: quem sai da lista não pode
            mais ser aberto para voltar. */}
        <button
          type="button"
          onClick={() => setIncludeArchived((shown) => !shown)}
          className="touch-target pt-1 text-[13px] text-accent"
        >
          {includeArchived ? 'Esconder arquivados' : 'Ver arquivados'}
        </button>
      </div>

      {!search && <Birthdays onOpen={setOpenId} />}

      {isPending && <Spinner />}

      {data && data.clients.length === 0 && (
        <EmptyState
          title="Nenhum cliente ainda"
          description="Cadastre no balcão conforme eles aparecem: nome, telefone, pronto. Em duas semanas a base está formada com dado real."
        />
      )}

      <ul className="px-4">
        {data?.clients.map((client) => (
          <li key={client.id}>
            <button
              type="button"
              onClick={() => setOpenId(client.id)}
              className="flex w-full items-center gap-3 border-b border-line py-3 text-left"
            >
              <Avatar name={client.name} photoUrl={client.photoUrl} size={44} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-medium text-ink">
                  {client.name}
                </span>
                <span className="block text-[13px] text-ink-faint">{client.phoneFormatted}</span>
              </span>
              <span className="flex shrink-0 gap-1.5">
                {client.archivedAt && <Badge>arquivado</Badge>}
                {/* Sem conseguir agendar até alguém aceitar o cadastro — e o
                    cliente só recebe "em análise" quando tenta marcar. */}
                {!client.approved && <Badge tone="warn">em análise</Badge>}
                {/* Revela sem abrir a ficha quem já tem tempo próprio e quem
                    ainda anda no padrão do serviço — é o que o barbeiro precisa
                    calibrar cliente a cliente. */}
                {client.customDurations > 0 ? (
                  <Badge tone="primary">
                    {client.customDurations} {client.customDurations === 1 ? 'tempo' : 'tempos'}
                  </Badge>
                ) : (
                  <Badge>tempo padrão</Badge>
                )}
                {client.totalNoShows > 0 && (
                  <Badge tone="danger">
                    {client.totalNoShows} {client.totalNoShows === 1 ? 'falta' : 'faltas'}
                  </Badge>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>

      <div className="pb-safe pointer-events-none sticky bottom-0 z-40 px-4 pt-2">
        <Button full className="pointer-events-auto shadow-lg" onClick={() => setCreating(true)}>
          Cadastrar cliente
        </Button>
      </div>

      {openId && <ClientSheet clientId={openId} onClose={() => setOpenId(null)} />}
      {creating && <NewClientSheet onClose={() => setCreating(false)} />}
    </div>
  );
}

/**
 * Aniversariantes do mês.
 *
 * O barbeiro já mandava parabéns pelo WhatsApp — de cabeça, e esquecendo
 * metade. A lista fica no topo de Clientes porque é onde ele já está quando
 * pensa nisso, e some durante a busca: quem está procurando alguém não quer
 * rolar por cima de outra coisa.
 *
 * Nada é enviado automaticamente. O botão abre o WhatsApp com a conversa certa
 * e ele escreve o que quiser — do mesmo jeito que o resto do app trata mensagem.
 */
function Birthdays({ onOpen }: { onOpen: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);

  const { data } = useQuery({
    queryKey: ['birthdays'],
    queryFn: () => api.get<{ birthdays: BirthdayClientView[] }>('/clients/birthdays'),
    staleTime: 60 * 60 * 1000,
  });

  const birthdays = data?.birthdays ?? [];
  if (birthdays.length === 0) return null;

  const today = birthdays.filter((entry) => entry.daysUntil === 0);

  // Quem já fez aniversário continua na lista — a seção é o mês inteiro — mas
  // fica no fim e sem botão: oferecer "parabenizar" dois dias depois faz o
  // barbeiro mandar mensagem atrasada achando que está em dia.
  const ordered = [
    ...birthdays.filter((entry) => entry.daysUntil >= 0),
    ...birthdays.filter((entry) => entry.daysUntil < 0),
  ];
  const shown = expanded ? ordered : ordered.slice(0, 3);

  return (
    <section className="px-4 pb-3">
      <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
        Aniversariantes do mês
        {today.length > 0 && <span className="pl-2 text-primary">· {today.length} hoje</span>}
      </h2>

      <ul className="overflow-hidden rounded-xl border border-line">
        {shown.map((entry) => (
          <li
            key={entry.id}
            className="flex items-center gap-3 border-b border-line bg-surface-raised px-3 py-2.5 last:border-b-0"
          >
            <button
              type="button"
              onClick={() => onOpen(entry.id)}
              className="flex min-w-0 flex-1 items-center gap-3 text-left"
            >
              <Avatar name={entry.name} photoUrl={entry.photoUrl} size={36} />
              <span className="min-w-0">
                <span className="block truncate text-[15px] text-ink">{entry.name}</span>
                <span className="block text-[12px] text-ink-faint">
                  {dayAndMonth(entry.date)} · faz {entry.turning}
                </span>
              </span>
            </button>

            {entry.daysUntil === 0 && (
              <a
                href={`https://wa.me/${entry.phone.replace(/\D+/g, '')}`}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 touch-target rounded-lg bg-primary px-3 py-1.5 text-[13px] font-semibold text-primary-ink"
              >
                Parabenizar
              </a>
            )}

            {entry.daysUntil > 0 && (
              <span className="shrink-0 text-[12px] text-ink-faint">
                em {entry.daysUntil}
                {entry.daysUntil === 1 ? ' dia' : ' dias'}
              </span>
            )}

            {entry.daysUntil < 0 && (
              <span className="shrink-0 text-[12px] text-ink-faint">já passou</span>
            )}
          </li>
        ))}
      </ul>

      {birthdays.length > 3 && (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          className="touch-target px-1 text-[13px] text-accent"
        >
          {expanded ? 'Mostrar menos' : `Ver todos os ${birthdays.length}`}
        </button>
      )}
    </section>
  );
}

function NewClientSheet({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [instagram, setInstagram] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [extras, setExtras] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const created = await api.post<{ client: ClientView }>('/clients', {
        name,
        phone,
        birthDate: birthDate || undefined,
        instagram: instagram.trim() || undefined,
      });

      // A foto vai depois porque só agora existe ficha para pendurá-la. Se
      // falhar, o cliente já está cadastrado — que é o que importava.
      if (photo) await api.put(`/clients/${created.client.id}/photo`, { data: photo });

      return created;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
      await queryClient.invalidateQueries({ queryKey: ['birthdays'] });
      onClose();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught : null),
  });

  return (
    <Sheet
      open
      onClose={onClose}
      title="Cliente novo"
      footer={
        <Button full loading={create.isPending} onClick={() => create.mutate()}>
          Cadastrar
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label="Nome"
          value={name}
          onChange={(event) => setName(event.target.value)}
          autoCapitalize="words"
          error={error?.fieldError('name')}
        />
        <Field
          label="Telefone"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          inputMode="tel"
          placeholder="(51) 99999-8888"
          error={error?.fieldError('phone')}
        />
        {extras ? (
          <>
            <div className="flex items-center gap-3">
              {photo ? (
                <img
                  src={`data:image/webp;base64,${photo}`}
                  alt=""
                  className="h-16 w-16 shrink-0 rounded-full object-cover"
                />
              ) : (
                <Avatar name={name || '?'} photoUrl={null} size={64} />
              )}
              <div className="flex-1">
                <PhotoPicker
                  label={photo ? 'Trocar foto' : 'Tirar foto'}
                  options={PHOTO_RESIZE}
                  facing="user"
                  onPicked={(data) => setPhoto(data)}
                />
              </div>
            </div>

            <Field
              label="Data de nascimento"
              type="date"
              value={birthDate}
              onChange={(event) => setBirthDate(event.target.value)}
              error={error?.fieldError('birthDate')}
            />

            <Field
              label="Instagram"
              value={instagram}
              onChange={(event) => setInstagram(event.target.value)}
              autoCapitalize="none"
              autoCorrect="off"
              placeholder="@perfil"
              error={error?.fieldError('instagram')}
            />
          </>
        ) : (
          <button
            type="button"
            onClick={() => setExtras(true)}
            className="touch-target self-start text-[14px] text-accent"
          >
            + Foto, aniversário e Instagram
          </button>
        )}

        {error && !error.fieldError('name') && !error.fieldError('phone') && (
          <ErrorNote>{error.message}</ErrorNote>
        )}
        <p className="text-[13px] text-ink-faint">
          O resto pode esperar. O tempo dele em cada serviço você ajusta depois do primeiro corte.
        </p>
      </div>
    </Sheet>
  );
}

function ClientSheet({ clientId, onClose }: { clientId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();
  const [editing, setEditing] = useState<string | null>(null);
  const [minutes, setMinutes] = useState('');
  const [waiting, setWaiting] = useState(false);
  const [editingCard, setEditingCard] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => api.get<{ client: ClientView }>(`/clients/${clientId}`),
  });

  const { data: services } = useQuery({
    queryKey: ['services'],
    queryFn: () => api.get<{ services: ServiceView[] }>('/services'),
  });

  const { data: suggestions } = useQuery({
    queryKey: ['client-suggestions', clientId],
    queryFn: () =>
      api.get<{ suggestions: DurationSuggestionView[] }>(`/clients/${clientId}/suggestions`),
  });

  const setDuration = useMutation({
    mutationFn: (input: { serviceId: string; minutes: number; fromSuggestion: boolean }) =>
      api.put(`/clients/${clientId}/durations`, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      await queryClient.invalidateQueries({ queryKey: ['client-suggestions', clientId] });
      setEditing(null);
    },
  });

  const resetLink = useMutation({
    mutationFn: () => api.post<{ whatsapp: { url: string } }>(`/clients/${clientId}/reset-link`),
    onSuccess: (result) => window.open(result.whatsapp.url, '_blank'),
  });

  const invalidateClient = async () => {
    await queryClient.invalidateQueries({ queryKey: ['client', clientId] });
    await queryClient.invalidateQueries({ queryKey: ['clients'] });
  };

  const approve = useMutation({
    mutationFn: () => api.patch(`/clients/${clientId}`, { approved: true }),
    onSuccess: invalidateClient,
  });

  /** Alterna: a mesma rota arquiva e desarquiva. */
  const archive = useMutation({
    mutationFn: () => api.post(`/clients/${clientId}/archive`),
    onSuccess: invalidateClient,
  });

  const client = data?.client;

  return (
    <Sheet open onClose={onClose} title={client?.name ?? 'Cliente'}>
      {isPending && <Spinner />}

      {client && (
        <div className="flex flex-col gap-5">
          <div className="flex items-center gap-4">
            <ZoomableAvatar
              name={client.name}
              photoUrl={client.photoUrl}
              size={72}
              detail={formatPhone(client.phone)}
            />

            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-[14px] text-ink-soft">{formatPhone(client.phone)}</span>
              {client.birthDate && (
                <span className="text-[13px] text-ink-faint">
                  Nasceu em {dayAndMonth(client.birthDate)}
                </span>
              )}
              {client.instagram && (
                <a
                  href={`https://instagram.com/${client.instagram}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[13px] text-accent"
                >
                  @{client.instagram}
                </a>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {client.hasAccount && <Badge tone="ok">usa o app</Badge>}
            {!client.approved && <Badge tone="warn">cadastro em análise</Badge>}
            {client.archivedAt && <Badge>arquivado</Badge>}
            {client.totalNoShows > 0 && (
              <Badge tone="danger">
                {client.totalNoShows} {client.totalNoShows === 1 ? 'falta' : 'faltas'}
              </Badge>
            )}
            <Badge>{client.totalAppointments} atendimentos</Badge>
          </div>

          <button
            type="button"
            onClick={() => setEditingCard(true)}
            className="touch-target self-start text-[14px] text-accent"
          >
            {client.photoUrl || client.birthDate || client.instagram
              ? 'Editar ficha'
              : '+ Foto, aniversário, Instagram e observações'}
          </button>

          {/* Enquanto ninguém aceita, o cliente leva "seu cadastro está em
              análise" ao tentar marcar e não tem a quem recorrer dentro do app. */}
          {!client.approved && (
            <div className="rounded-xl border border-warn/40 bg-warn/10 p-4">
              <p className="text-[14px] text-ink">
                Este cadastro ainda não foi aceito, e enquanto isso {client.name} não consegue
                agendar pelo app.
              </p>
              <Button
                className="mt-3"
                full
                loading={approve.isPending}
                onClick={() => approve.mutate()}
              >
                Aceitar cadastro
              </Button>
            </div>
          )}

          <ClientGallery clientId={clientId} />

          {/* Sugestões vindas do tempo real medido */}
          {suggestions?.suggestions.map((suggestion) => (
            <div
              key={suggestion.serviceId}
              className="rounded-xl border border-primary/40 bg-primary/10 p-4"
            >
              <p className="text-[14px] text-ink">
                Nos últimos {suggestion.sampleCount} atendimentos de{' '}
                <span className="font-semibold">{suggestion.serviceName}</span> você levou em média{' '}
                <span className="font-semibold">{suggestion.observedAverageMinutes} min</span>. O
                cadastro diz {suggestion.currentMinutes} min.
              </p>
              <Button
                className="mt-3"
                full
                loading={setDuration.isPending}
                onClick={() =>
                  setDuration.mutate({
                    serviceId: suggestion.serviceId,
                    minutes: suggestion.suggestedMinutes,
                    fromSuggestion: true,
                  })
                }
              >
                Atualizar para {suggestion.suggestedMinutes} min
              </Button>
            </div>
          ))}

          <section>
            <h3 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
              Tempo por serviço
            </h3>

            <div className="overflow-hidden rounded-xl border border-line">
              {services?.services
                .filter((service) => service.active)
                .map((service) => {
                  const custom = client.durations.find((entry) => entry.serviceId === service.id);
                  const value = custom?.minutes ?? service.defaultDurationMinutes;
                  const isEditing = editing === service.id;

                  return (
                    <div
                      key={service.id}
                      className="flex items-center justify-between gap-3 border-b border-line bg-surface-raised px-4 py-3 last:border-b-0"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-[15px] text-ink">{service.name}</p>
                        <p className="text-[12px] text-ink-faint">
                          {custom
                            ? `personalizado · padrão ${duration(service.defaultDurationMinutes)}`
                            : 'usa o padrão do serviço'}
                        </p>
                      </div>

                      {isEditing ? (
                        <div className="flex shrink-0 items-center gap-2">
                          <input
                            type="number"
                            inputMode="numeric"
                            min={5}
                            max={480}
                            step={5}
                            value={minutes}
                            onChange={(event) => setMinutes(event.target.value)}
                            className="w-20 rounded-lg border border-line bg-surface px-2 py-2 text-center text-[16px] tabular-nums text-ink"
                          />
                          <Button
                            className="px-3 py-2"
                            loading={setDuration.isPending}
                            onClick={() =>
                              setDuration.mutate({
                                serviceId: service.id,
                                minutes: Number(minutes),
                                fromSuggestion: false,
                              })
                            }
                          >
                            Ok
                          </Button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(service.id);
                            setMinutes(String(value));
                          }}
                          className={`touch-target shrink-0 rounded-lg px-3 text-[15px] font-semibold tabular-nums ${
                            custom ? 'text-primary' : 'text-ink-soft'
                          }`}
                        >
                          {duration(value)}
                        </button>
                      )}
                    </div>
                  );
                })}
            </div>
            <p className="px-1 pt-2 text-[12px] text-ink-faint">
              Toque no tempo para ajustar. É esse número que a agenda usa ao oferecer horários.
            </p>
          </section>

          {client.notes && (
            <div className="rounded-xl border border-line bg-surface-raised px-4 py-3">
              <p className="pb-1 text-[12px] font-semibold text-ink-faint">Observações</p>
              <p className="text-[14px] text-ink">{client.notes}</p>
            </div>
          )}

          {/* "Me deixa fixo na terça" também é dito com o cliente na cadeira. */}
          <ClientSeriesSection clientId={client.id} clientName={client.name} />

          <div className="flex flex-col gap-2">
            {/* A frase que originou isto é dita aqui, com o cliente na cadeira:
                "deixa na espera aí". Por isso o botão mora na ficha. */}
            <Button variant="secondary" full onClick={() => setWaiting(true)}>
              Aguardar vaga
            </Button>

            <a
              href={`https://wa.me/${client.phone.replace(/\D+/g, '')}`}
              target="_blank"
              rel="noreferrer"
            >
              <Button variant="secondary" full>
                Chamar no WhatsApp
              </Button>
            </a>

            {client.hasAccount && (
              <Button
                variant="ghost"
                full
                loading={resetLink.isPending}
                onClick={() => resetLink.mutate()}
              >
                Gerar link de nova senha
              </Button>
            )}

            {/* Arquivar, nunca excluir: o histórico é ativo da barbearia, e
                quem some da lista volta inteiro no dia em que reaparecer. */}
            <Button
              variant={client.archivedAt ? 'secondary' : 'danger'}
              full
              loading={archive.isPending}
              onClick={() => archive.mutate()}
            >
              {client.archivedAt ? 'Trazer de volta' : 'Arquivar cliente'}
            </Button>
          </div>

          <p className="text-center text-[12px] text-ink-faint">
            Arquivar tira da lista sem apagar nada: atendimentos, fotos e tempos continuam aqui.
          </p>

          <p className="text-center text-[12px] text-ink-faint">
            Fuso da barbearia: {tenant.timezone}
          </p>

          {waiting && (
            <NewWaitlistSheet
              clientId={client.id}
              clientName={client.name}
              onClose={() => setWaiting(false)}
            />
          )}

          {editingCard && <EditClientSheet client={client} onClose={() => setEditingCard(false)} />}
        </div>
      )}
    </Sheet>
  );
}

/**
 * A ficha inteira, editável.
 *
 * Abre em folha própria porque não é o que o barbeiro veio fazer aqui — ele
 * veio ajustar o tempo do cliente, e o formulário aberto por padrão empurraria
 * essa parte para fora da tela. Mas quando o cliente troca de número no meio da
 * conversa, corrigir tem que ser um toque, não uma ida a outro lugar.
 *
 * A foto sobe sozinha, assim que escolhida: ela chega pela câmera, e segurá-la
 * esperando o "Salvar" do rodapé só cria a chance de perdê-la se a folha for
 * fechada por engano.
 */
function EditClientSheet({ client, onClose }: { client: ClientView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(client.name);
  // Formatado, não em E.164: o barbeiro confere o número lendo, e `phoneSchema`
  // normaliza de volta o que voltar daqui.
  const [phone, setPhone] = useState(formatPhone(client.phone));
  const [birthDate, setBirthDate] = useState(client.birthDate ?? '');
  const [instagram, setInstagram] = useState(client.instagram ?? '');
  const [notes, setNotes] = useState(client.notes ?? '');
  const [error, setError] = useState<ApiError | null>(null);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['client', client.id] });
    await queryClient.invalidateQueries({ queryKey: ['clients'] });
    await queryClient.invalidateQueries({ queryKey: ['birthdays'] });
    // O nome e a foto do cliente aparecem em cima de cada atendimento.
    await queryClient.invalidateQueries({ queryKey: ['agenda'] });
  };

  const savePhoto = useMutation({
    mutationFn: (data: string) => api.put(`/clients/${client.id}/photo`, { data }),
    onSuccess: invalidate,
    onError: (caught) => setError(caught instanceof ApiError ? caught : null),
  });

  const removePhoto = useMutation({
    mutationFn: () => api.delete(`/clients/${client.id}/photo`),
    onSuccess: invalidate,
  });

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/clients/${client.id}`, {
        name,
        phone,
        birthDate: birthDate || null,
        instagram: instagram.trim() || null,
        notes: notes.trim() || null,
      }),
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught : null),
  });

  const inField = ['name', 'phone', 'birthDate', 'instagram', 'notes'].some((field) =>
    error?.fieldError(field),
  );

  return (
    <Sheet
      open
      onClose={onClose}
      title="Editar ficha"
      footer={
        <Button full loading={save.isPending} onClick={() => save.mutate()}>
          Salvar
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <ZoomableAvatar
            name={name || client.name}
            photoUrl={client.photoUrl}
            size={64}
            detail={formatPhone(client.phone)}
          />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <PhotoPicker
              label={client.photoUrl ? 'Trocar foto' : 'Tirar foto'}
              options={PHOTO_RESIZE}
              facing="user"
              busy={savePhoto.isPending}
              onPicked={(data) => savePhoto.mutateAsync(data)}
            />
            {client.photoUrl && (
              <button
                type="button"
                onClick={() => removePhoto.mutate()}
                className="touch-target self-start text-[13px] text-ink-faint"
              >
                Remover foto
              </button>
            )}
          </div>
        </div>

        <Field
          label="Nome"
          value={name}
          onChange={(event) => setName(event.target.value)}
          autoCapitalize="words"
          hint="Como você reconhece a pessoa — o apelido vale mais que o nome de batismo"
          error={error?.fieldError('name')}
        />

        <Field
          label="Telefone"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          inputMode="tel"
          placeholder="(51) 99999-8888"
          hint={
            client.hasAccount
              ? 'Este cliente usa o app: trocar aqui troca também o número com que ele entra'
              : undefined
          }
          error={error?.fieldError('phone')}
        />

        <Field
          label="Data de nascimento"
          type="date"
          value={birthDate}
          onChange={(event) => setBirthDate(event.target.value)}
          error={error?.fieldError('birthDate')}
        />

        <Field
          label="Instagram"
          value={instagram}
          onChange={(event) => setInstagram(event.target.value)}
          autoCapitalize="none"
          autoCorrect="off"
          placeholder="@perfil"
          error={error?.fieldError('instagram')}
        />

        <TextArea
          label="Observações"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Ex.: alérgico a talco, prefere máquina 2, chega sempre adiantado"
          hint="Só a equipe vê. O cliente nunca lê isto."
          error={error?.fieldError('notes')}
        />

        {error && (error.status === 409 || !inField) && <ErrorNote>{error.message}</ErrorNote>}
      </div>
    </Sheet>
  );
}

/**
 * O histórico visual do cliente.
 *
 * São as fotos dos atendimentos dele, da mais recente para a mais antiga — a
 * resposta para "como foi aquele corte que ele gostou?". Não há cópia: é a
 * mesma foto que vive no atendimento, lida em outra ordem.
 */
function ClientGallery({ clientId }: { clientId: string }) {
  const { tenant } = useTenant();
  const [zoomed, setZoomed] = useState<ClientPhotoView | null>(null);

  const { data } = useQuery({
    queryKey: ['client-photos', clientId],
    queryFn: () => api.get<{ photos: ClientPhotoView[] }>(`/clients/${clientId}/photos`),
  });

  const photos = data?.photos ?? [];
  if (photos.length === 0) return null;

  return (
    <section>
      <h3 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
        Fotos dos atendimentos
      </h3>

      <div className="scroll-touch -mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
        {photos.map((photo) => (
          <button
            key={photo.id}
            type="button"
            onClick={() => setZoomed(photo)}
            className="relative shrink-0"
          >
            <img
              src={photo.url}
              alt=""
              className="h-28 w-28 rounded-xl object-cover"
              loading="lazy"
            />
            <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white">
              {PHOTO_MOMENT_LABELS[photo.moment]}
            </span>
          </button>
        ))}
      </div>

      {zoomed && (
        <Sheet open onClose={() => setZoomed(null)} title={zoomed.serviceName}>
          <img src={zoomed.url} alt="" className="w-full rounded-xl object-contain" />
          <p className="pt-3 text-center text-[13px] text-ink-faint">
            {PHOTO_MOMENT_LABELS[zoomed.moment]} ·{' '}
            {new Intl.DateTimeFormat('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              timeZone: tenant.timezone,
            }).format(new Date(zoomed.appointmentStartsAt))}
          </p>
        </Sheet>
      )}
    </section>
  );
}
